/* ============================================================================
   faq.js — <sf-faq> (sections/faq.liquid)
   ----------------------------------------------------------------------------
   Replaces the section's inline toggle script, which read reduced motion once
   at load and hid a panel from a 'transitionend' listener it never cancelled:
   close-then-reopen within the transition hid the panel it had just reopened,
   and a close that started no transition never hid at all.

   The motion is a CSS transition on grid-template-rows (section-faq.css). CSS
   transitions already reverse from the value they are at when the target flips
   back mid-way, so this element never measures heights; it only keeps three
   things in step for each question:
     • the button's aria-expanded   (what the click asked for, set at once)
     • the panel's .is-open         (what the CSS animates towards)
     • the panel's `hidden`         (removed before an open, set only once a
                                     close has really finished, so a closed
                                     answer is neither focusable nor read)
   Opening from `hidden`, the 0fr start is committed as a computed style before
   .is-open is added; otherwise display:none → 1fr can land in one style pass
   and the panel snaps open (it did when opened with the Space key).
   Whether a close has finished is read from the panel's own running
   transitions (getAnimations), never from a remembered listener, so a stale
   end event from an earlier toggle can do nothing, and a close that runs no
   transition (reduced motion, already shut) is hidden immediately.

   Reduced motion is read on every toggle, and a change of preference while
   something is moving lands it on its end state. Theme editor:
   shopify:block:select opens that question (at once after a re-render, and
   not straight after a click on it). Vanilla ES2020, no globals; one
   AbortController per connection (CONTRACT §0.6), so a re-render or a move
   can never bind twice; the definition is guarded because every FAQ section on
   a page loads this file.
   ============================================================================ */
(() => {
  'use strict';
  if (customElements.get('sf-faq')) return;

  const ROWS = 'grid-template-rows';
  /* the editor's selection round trip after a preview click, with room to spare */
  const SELECT_AFTER_CLICK_MS = 1000;
  const reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  /* html.motion-ok is what the CSS transition hangs on (theme.js keeps it in step with the preference); the query is
     read too, so a change is honoured even before theme.js has reacted to it */
  const motionOff = () => !document.documentElement.classList.contains('motion-ok') || !!(reduceQuery && reduceQuery.matches);

  class SFFaq extends HTMLElement {
    connectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      this._ctrl = new AbortController();
      const signal = this._ctrl.signal;

      /* The markup already agrees with itself; this only repairs a copy that was re-attached mid-motion. */
      this._rows().forEach(({ btn, panel }) => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        if (panel.classList.contains('is-open') !== open || panel.hidden === open) this._set(btn, panel, open, false);
      });

      this.addEventListener('click', (e) => {
        const btn = e.target && e.target.closest ? e.target.closest('[data-faq-btn]') : null;
        if (!btn || !this.contains(btn)) return;
        const panel = this._panelFor(btn);
        if (!panel) return;
        this._clicked = { btn, at: performance.now() };
        this._set(btn, panel, btn.getAttribute('aria-expanded') !== 'true', !motionOff());
      }, { signal });

      /* only the panel's own row transition counts: these events bubble up from anything inside the answer */
      const ended = (e) => {
        if (e.propertyName !== ROWS || !e.target.classList || !e.target.classList.contains('faq__panel')) return;
        if (this.contains(e.target)) this._settle(e.target);
      };
      this.addEventListener('transitionend', ended, { signal });
      this.addEventListener('transitioncancel', ended, { signal });

      if (reduceQuery && reduceQuery.addEventListener) {
        reduceQuery.addEventListener('change', () => {
          if (!motionOff()) return;
          this._rows().forEach(({ btn, panel }) => this._set(btn, panel, btn.getAttribute('aria-expanded') === 'true', false));
        }, { signal });
      }

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
          const item = e.target;
          if (!item || !this.contains(item)) return;
          const btn = item.querySelector('[data-faq-btn]');
          const panel = btn && this._panelFor(btn);
          if (!panel || btn.getAttribute('aria-expanded') === 'true') return;
          /* a click on a question in the preview also selects its block, so the select that follows a click
             that closed it must not open it again: the click is the merchant's latest request */
          const c = this._clicked;
          if (c && c.btn === btn && performance.now() - c.at < SELECT_AFTER_CLICK_MS) return;
          /* detail.load: the editor re-rendered the section after a setting change and is re-selecting the block
             being edited; that answer should simply be open again, not grow back on every keystroke */
          this._set(btn, panel, true, !motionOff() && !(e.detail && e.detail.load));
        }, { signal });
      }
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      this._ctrl = null;
    }

    _rows() {
      return Array.from(this.querySelectorAll('[data-faq-btn]'))
        .map((btn) => ({ btn, panel: this._panelFor(btn) }))
        .filter((r) => r.panel);
    }

    _panelFor(btn) {
      const id = btn.getAttribute('aria-controls');
      return id ? this.querySelector('#' + CSS.escape(id)) : null;
    }

    /**
     * Move one question to `open`. aria-expanded follows the request at once; the panel animates towards it
     * from wherever it is, or jumps there when `animate` is false.
     * @param {HTMLElement} btn @param {HTMLElement} panel @param {boolean} open @param {boolean} animate
     */
    _set(btn, panel, open, animate) {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!animate) {
        /* transitions off for the jump, and the jump committed before they come back, so it cannot animate late */
        const hadFocus = !open && this._holdsFocus(btn, panel);
        panel.style.transition = 'none';
        panel.classList.toggle('is-open', open);
        panel.hidden = !open;
        void panel.offsetHeight;
        panel.style.removeProperty('transition');
        if (!panel.getAttribute('style')) panel.removeAttribute('style');
        if (hadFocus) btn.focus({ preventScroll: true });
        return;
      }
      if (open) {
        if (panel.hidden) {
          panel.hidden = false;
          void panel.offsetHeight; /* the 0fr start state, as a computed style, before the 1fr target */
        }
        panel.classList.add('is-open');
      } else {
        panel.classList.remove('is-open');
        this._settle(panel);
      }
    }

    /** Hide a closed panel once nothing is animating its rows any more (now, or at its own transition's end). */
    _settle(panel) {
      if (panel.hidden || panel.classList.contains('is-open')) return;
      if (typeof panel.getAnimations === 'function' &&
          panel.getAnimations().some((a) => a.transitionProperty === ROWS && a.playState === 'running')) return;
      const btn = this.querySelector('[aria-controls="' + CSS.escape(panel.id) + '"]');
      const hadFocus = btn && this._holdsFocus(btn, panel);
      panel.hidden = true;
      if (hadFocus) btn.focus({ preventScroll: true });
    }

    /* focus inside an answer that is about to be hidden would fall back to <body>; the question takes it instead */
    _holdsFocus(btn, panel) {
      const a = document.activeElement;
      return !!(a && a !== btn && panel.contains(a));
    }
  }

  customElements.define('sf-faq', SFFaq);
})();
